import { SqlGuardError, validateReadOnlySql } from "@/lib/market-data-db/sql-guard";
import type { OHLCData } from "@/vybe-trader-client/chart/types";

export interface InMemorySqlResult {
  columns: string[];
  rows: unknown[][];
  rowCount: number;
  truncated: boolean;
}

const COL_MAP: Record<string, keyof OHLCData> = {
  ts: "timestamp",
  timestamp: "timestamp",
  open: "open",
  high: "high",
  low: "low",
  close: "close",
  volume: "volume",
};

function parseSymbolFilter(sql: string): string | null {
  const m = sql.match(/symbol\s*=\s*'([^']+)'/i);
  return m?.[1] ?? null;
}

function parseTsBounds(sql: string): { fromTs?: number; toTs?: number } {
  const out: { fromTs?: number; toTs?: number } = {};
  const gte = sql.match(/(?:ts|timestamp)\s*>=\s*(\d+)/i);
  const lte = sql.match(/(?:ts|timestamp)\s*<=\s*(\d+)/i);
  if (gte) out.fromTs = Number(gte[1]);
  if (lte) out.toTs = Number(lte[1]);
  return out;
}

function parseLimit(sql: string, cap: number): number {
  const m = sql.match(/\blimit\s+(\d+)/i);
  if (!m) return cap;
  return Math.min(Math.max(1, parseInt(m[1]!, 10)), cap);
}

function parseOrderDesc(sql: string): boolean {
  return /\border\s+by\s+(?:ts|timestamp)\s+desc\b/i.test(sql);
}

function parseSelectColumns(sql: string): string[] {
  const m = sql.match(/select\s+(.+?)\s+from\b/is);
  if (!m) return ["ts", "open", "high", "low", "close", "volume"];
  const part = m[1]!.trim();
  if (part === "*") return ["ts", "open", "high", "low", "close", "volume"];
  return part.split(",").map((c) => {
    const t = c.trim().toLowerCase();
    const asMatch = t.match(/\bas\s+(\w+)$/);
    return asMatch ? asMatch[1]! : t.replace(/[^a-z0-9_]/g, "");
  });
}

export function executeInMemorySql(
  sql: string,
  bars: OHLCData[],
  rowCap: number,
): InMemorySqlResult {
  const trimmed = validateReadOnlySql(sql);
  if (!/from\s+bars(_1m)?\b/i.test(trimmed)) {
    throw new SqlGuardError("Query must use FROM bars_1m or FROM bars");
  }
  if (!parseSymbolFilter(trimmed)) {
    throw new SqlGuardError("Include symbol = 'PAIR' in WHERE clause");
  }

  const { fromTs, toTs } = parseTsBounds(trimmed);
  const limit = parseLimit(trimmed, rowCap);
  const desc = parseOrderDesc(trimmed);
  const columns = parseSelectColumns(trimmed);

  let filtered = [...bars];
  if (fromTs != null) filtered = filtered.filter((b) => b.timestamp >= fromTs);
  if (toTs != null) filtered = filtered.filter((b) => b.timestamp <= toTs);

  filtered.sort((a, b) => (desc ? b.timestamp - a.timestamp : a.timestamp - b.timestamp));
  const truncated = filtered.length > limit;
  const sliced = filtered.slice(0, limit);
  if (!desc) sliced.sort((a, b) => a.timestamp - b.timestamp);

  const rows = sliced.map((bar) =>
    columns.map((col) => {
      const key = COL_MAP[col] ?? col;
      return key === "timestamp"
        ? bar.timestamp
        : (bar as unknown as Record<string, unknown>)[key];
    }),
  );

  return { columns, rows, rowCount: rows.length, truncated };
}
